/* eslint-disable arrow-parens */
import React, { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FormControl, Button, InputAdornment } from "@material-ui/core";
import { trackPromise } from "react-promise-tracker";

// other components
import ROLES from "../../../../constants/roles";
import { ThemeCustomInputField } from "../../../shared/themeComponents/themeCustomInputField";
import { api } from "../../../../utils/api";
import validateEmail from "../../../../utils/commonFunctions/validateEmail";
import SelectMenuItems from "../../../shared/selectMenuItems/SelectMenuItems";
import CopyrightFooterLink from "../authFooter/CopyrightFooterLink";
import SignInFooterLink from "../authFooter/SignInFooterLink";
import { Validation } from "../../../../@types/validation.types";

// icons and images
import AccountCircle from "@material-ui/icons/AccountCircle";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { IoSend } from "react-icons/io5";
import { nexcaliberLogo } from "../../../../assets";
import { forgetPasswordUsernameImage } from "../../../../assets";

import "./forgotUsername.scss";
import { ADMIN_ENV } from "../../../../config/config";

const ForgotUsername = () => {
	const [anchorElement, setAnchorElement] = useState<(EventTarget & HTMLButtonElement) | null>(null);
	const [hasCreateClick, setHasCreateClick] = useState(false);
	const [forgetUsernameDetails, setForgetUsernameDetails] = useState({
		role: "",
		email: ""
	});
	const [validation, setValidation] = useState<Validation>({
		forgetUsernameDetails: {},
		status: "invalid"
	});

	const _validation = useRef<Validation>();
	const navigate = useNavigate();
	const roles = Object.values(ROLES);

	const handleValidation = useCallback(async () => {
		const { role, email } = forgetUsernameDetails;
		_validation.current = {
			...validation,
			forgetUsernameDetails: {},
			status: "invalid"
		};
		let flag = true;

		if (role.length === 0) {
			_validation.current.forgetUsernameDetails["role"] = "Role is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		if (email.length === 0) {
			_validation.current.forgetUsernameDetails["email"] = "Email is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		if (email.length !== 0) {
			if (!validateEmail(email)) {
				_validation.current.forgetUsernameDetails["email"] = "Valid Email is required";
				_validation.current["status"] = "invalid";
				flag = false;
			}
		}

		if (flag === true) _validation.current["status"] = "valid";
		else {
			_validation.current["status"] = "invalid";
			alert("Please, fill all required fields!");
		}
		setValidation(Object.assign({}, _validation.current));
		return _validation.current["status"];
	}, [forgetUsernameDetails, validation]);

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = event.target;

		setForgetUsernameDetails(Object.assign({}, forgetUsernameDetails, { [name]: value }));
	};

	const handleRoleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		const { currentTarget } = event;
		setAnchorElement(currentTarget);
	};

	const handleCredentialRoleChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>, name: string) => {
			setAnchorElement(null);
			const value = event.currentTarget.getAttribute("value");
			setForgetUsernameDetails(Object.assign({}, forgetUsernameDetails, { [name]: value }));
		},
		[forgetUsernameDetails]
	);

	const handleRoleMenuClose = () => {
		setAnchorElement(null);
	};

	// SEND USERNAME LINK - BUTTON
	const handleSendUsernameButtonClick = async (event: React.FormEvent<HTMLFormElement>) => {
		try {
			event.preventDefault();
			const validation = await handleValidation();
			if (validation === "valid") {
				const payload = {
					...forgetUsernameDetails,
					role: forgetUsernameDetails.role.toUpperCase()
				};
				const response = await trackPromise(api.auth.forgetUserName(payload));

				if (response) {
					setHasCreateClick(true);
					alert("Username sent to the given email!");
					navigate("/login");
				}
			} else {
				setHasCreateClick(false);
			}
		} catch (error) {
			setHasCreateClick(false);
			alert("Unable to sent email!");
			throw error;
		}
	};

	return (
		<div className="forget-username-main-container" id="forget-username-main-container">
			<div className="left-section">
				<img className="illustration" src={forgetPasswordUsernameImage} alt="" />
				<div className="tagline">
					<h3>Recoveries . . .</h3>
					<span>are very simple in our portal</span>
				</div>
			</div>
			<div className="right-section">
				{ADMIN_ENV === "DEV" ? (
					<div className="test-portal-login-container" style={{ marginTop: "20px" }}>
						<h2 className="test-portal-login-text">Test Portal</h2>
					</div>
				) : null}
				<img className="nexcaliber-logo" src={nexcaliberLogo} alt="" />
				<h3>
					Username <span>Recovery</span>
				</h3>
				<span className="subheading">This will generate you the registered username.</span>
				<form onSubmit={handleSendUsernameButtonClick} autoComplete="off" method="">
					<FormControl className="form-container" id="form-container">
						<div className="select-role-container" id="select-role-container">
							<Button
								onClick={handleRoleClick}
								endIcon={<ExpandMoreIcon className="expand-icon" />}
								className="role-select-button"
							>
								{forgetUsernameDetails.role !== "" ? forgetUsernameDetails.role : "Select Your Role"}
							</Button>
							<SelectMenuItems
								fieldName="role"
								anchorElement={anchorElement}
								listItems={roles}
								handleMenuClose={handleRoleMenuClose}
								handleSelecMenuItemClick={handleCredentialRoleChange}
							/>
							{validation.forgetUsernameDetails.role ? (
								<div className="validation-details">
									<span className="select-validation-text">
										{validation.forgetUsernameDetails.role}
									</span>
								</div>
							) : null}
						</div>
						<ThemeCustomInputField
							className="form-field-input"
							id="email-input"
							label="Enter your username/ email"
							name="email"
							variant="outlined"
							value={forgetUsernameDetails.email}
							onChange={handleChange}
							helperText={validation.forgetUsernameDetails["email"]}
							InputProps={{
								endAdornment: (
									<InputAdornment position="start">
										<AccountCircle className="adornment-color" />
									</InputAdornment>
								)
							}}
						/>
						<div className="forget-username-button-container" id="forget-username-button-container">
							<Button
								className="forget-username-button"
								variant="contained"
								type="submit"
								disabled={hasCreateClick}
								disableElevation={hasCreateClick}
							>
								<span className="button-label-with-icon">Send Username</span>
								<span>
									<IoSend className="button-icon" />
								</span>
							</Button>
						</div>
					</FormControl>
				</form>
				<SignInFooterLink styleClass="sign-in-container" />
				<CopyrightFooterLink styleClass="copyright-text-container" />
			</div>
		</div>
	);
};

export default ForgotUsername;
